/* eslint-disable indent */

/**
 * Auth Utils
 */
import jwtDecode from 'jwt-decode';
import { publicService, publicGWService, pvtGWSservice } from './request';
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace NodeJS {
    interface Global {
      window: any;
      localStorage: {
        getItem: (key: string) => any;
        setItem: (key: string, value: string) => any;
      };
    }
  }
}

type LoginUserProps = {
  // User's email
  email: string;
  // User's password
  password: string;
  // Redirect the user on success to this link
  redirectUrl?: string;
  // Code from slack to link the account
  // to the Slack profile
  slackToken?: string;
};

type UserAuthLinkTokenInput = {
  provider: 'SLACK';
  token: string;
  type: 'USER' | 'ACCOUNT';
};

type LoginInputDataType = {
  email: string;
  password: string;
  linkTokens?: UserAuthLinkTokenInput[];
};

const login = async ({ email, password, redirectUrl }: LoginUserProps) => {
  try {
    const inputData: LoginInputDataType = {
      email,
      password,
    };

    const handleLink = `mutation loginUser {
      loginUser(input: ${JSON.stringify(inputData)
        .replace(/"([^(")"]+)":/g, '$1:')
        .replace('"SLACK"', 'SLACK')
        .replace('"USER"', 'USER')
        .replace('"ACCOUNT"', 'ACCOUNT')}) {
        accessToken
      }
    }`;

    const {
      data: {
        data: {
          loginUser: { accessToken },
        },
      },
    } = await publicGWService('/graphql', {
      method: 'post',
      data: {
        query: handleLink,
      },
    });
    setLoginToken({ token: accessToken }, false, redirectUrl);
    return { success: true, error: null };
  } catch (error) {
    console.log('error', error);
    return { success: false, error: 'The email address or password you entered is incorrect' };
  }
};

const signUp = async data => {
  try {
    await publicService('/v1/auth/sign_up', {
      method: 'post',
      data,
    });
    return { success: true, error: '' };
  } catch (error) {
    return { success: false, error: 'Error with your email and/or password' };
  }
};

const graphqlSignUp = async data => {
  // List down all possible fields for easy understanding
  try {
    const {
      invitationId,
      firstName,
      lastName,
      timeZone,
      email,
      password,
      accountId,
      utmId,
      countryId,
      practitionerType,
    } = data;

    const query = `mutation registerUser {
       registerUser(input: {
         ${accountId ? `accountId: ${accountId},` : ''}
         ${invitationId ? `invitationId: "${invitationId}",` : ''}
         email: "${email}",
         password: "${password}",
         firstName: "${firstName}",
         lastName: "${lastName}",
         platformAccess: { platform: CONNECT },
         role: USER,
         timeZone: "${timeZone}",
         countryId: "${countryId}",
         practitionerType: ${practitionerType},
          ${utmId ? `, utmId: "${utmId}"` : ''}
       }) {
         accessToken
       }
     }`;

    const res: any = await publicGWService('/graphql', {
      method: 'post',
      data: {
        query,
      },
    });
    const { data: resData, errors } = res.data || {};
    if (resData?.registerUser?.accessToken) {
      // setLoginToken({ token: resData.registerUser.accessToken }, true);
      return { success: true, error: '' };
    } else if (errors) {
      return { success: false, error: errors[0]?.message };
    } else {
      return { success: false, error: 'Unable to signup with this email / password' };
    }
  } catch (error) {
    return { success: false, error: 'Something went wrong please try again later' };
  }
};

const setLoginToken = ({ token }, noRedirect: boolean, redirectUrl?: string) => {
  window.localStorage.setItem('token', token);
  if (!noRedirect) {
    if (redirectUrl) {
      window.open(redirectUrl, '_self');
    } else {
      window.open('/', '_self');
    }
  }
};

const getAuthState = () => {
  const token: string | null = window.localStorage.getItem('token');
  if (token) {
    return token;
  }
  return null;
};

const isTokenExpired = token => {
  const decodedToken = jwtDecode(token);
  // convert unix to date
  const expiry = new Date(decodedToken.exp * 1000);
  const now = new Date();
  if (expiry >= now) {
    return false;
  } else {
    return true;
  }
};

const verifyAuthentification = async () => {
  const token = getAuthState();
  if (token) {
    if (isTokenExpired(token)) return { success: false, message: 'Token has expired' };
    else {
      return { success: true, message: 'Welcome back' };
    }
  } else {
    return { success: false, message: 'No token found' };
  }
};

const logout = async () => {
  try {
    const data = await pvtGWSservice('/graphql', {
      method: 'post',
      data: {
        query: `mutation logoutUser { logoutUser }`,
      },
    });
    if (data) {
      window.localStorage.removeItem('token');
      window.localStorage.setItem('logout', Date.now().toString());
      window.open('/login', '_self');
      return true;
    }
  } catch (error) {
    console.log('error', error);
  }
};

const getUserId: () => string = () => {
  const token: string | null = window.localStorage.getItem('token');

  if (token) {
    const { user_id: userId } = jwtDecode(token);
    return userId as string;
  }
  return '';
};

const getUserName: () => string = () => {
  const token: string | null = window.localStorage.getItem('token');

  if (token) {
    const decodedToken = jwtDecode(token);
    return decodedToken.firstName as string;
  }
  return '';
};

const getAccountId: () => string = () => {
  const token: string | null = window.localStorage.getItem('token');

  if (token) {
    const { account_id: accountId } = jwtDecode(token);
    return accountId as string;
  }
  return '';
};

const convertID = (id: string): string => {
  const w = id.split('-');
  return w[1];
};

const convertIDInt = (id: string): number => {
  if (id.indexOf('-') > -1) {
    const w = id.split('-');
    return parseInt(w[1]);
  }

  return parseInt(id);
};

const convertIDString = (id: number | string, table: string): string => {
  return `${table}-${id}`;
};

export {
  login,
  signUp,
  graphqlSignUp,
  logout,
  setLoginToken,
  getAuthState,
  verifyAuthentification,
  getUserId,
  getUserName,
  getAccountId,
  convertID,
  convertIDInt,
  convertIDString,
};
